import React, {
  type KeyboardEvent,
  useCallback,
  useContext,
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Button,
  Center,
  Grid,
  GridItem,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  type ResponsiveValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../app/App';
import { paths } from '../../util/routes';
import { IMAGE_URI } from '../../service/constants';
import { useDeletePhoto } from '../../service/requests';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Photo } from '../../util/types';

interface PhotoGalleryProps {
  photos: Photo[];
}

enum ImageOrientation {
  LANDSCAPE,
  PORTRAIT,
}

interface ImageMeta {
  photoId: string;
  img: HTMLImageElement;
  width: number;
  height: number;
  orientation: ImageOrientation;
  rowSpan: number;
  modalSize:
    | ResponsiveValue<
        | '6xl'
        | 'lg'
        | (string & unknown)
        | 'sm'
        | 'md'
        | 'xl'
        | '2xl'
        | 'xs'
        | 'full'
        | '3xl'
        | '4xl'
        | '5xl'
      >
    | undefined;
}

const getImageSize = async (
  url: string,
): Promise<{ width: number; height: number; img: HTMLImageElement }> =>
  await new Promise((resolve) => {
    const img = new window.Image();
    img.src = url;
    img.onload = () => {
      resolve({ width: img.width, height: img.height, img });
    };
  });

export const PhotoGallery = ({ photos }: PhotoGalleryProps): JSX.Element => {
  const { user } = useContext(UserContext);
  const { deletePhoto } = useDeletePhoto();
  const navigate = useNavigate();

  const [modalPhotoId, setModalPhotoId] = useState<string | null>(null);
  const [displayImageBorder, setDisplayImageBorder] = useState<boolean>(false);
  const [images, setImages] = useState<ImageMeta[]>([]);

  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!modalPhotoId) {
        return;
      }

      const indexOfModalImage = photos.findIndex(
        (photo: Photo) => photo.photoId === modalPhotoId,
      );
      switch (event.key) {
        case 'ArrowRight':
          if (indexOfModalImage === photos.length - 1) {
            return;
          }
          setModalPhotoId(photos[indexOfModalImage + 1].photoId);
          return;
        case 'ArrowLeft':
          if (indexOfModalImage === 0) {
            return;
          }
          setModalPhotoId(photos[indexOfModalImage - 1].photoId);
      }
    },
    [modalPhotoId],
  );

  useEffect(() => {
    const loadImages = async (): Promise<void> => {
      const imageData = await Promise.all(
        photos.map(async ({ photoId }) => {
          const url = `${IMAGE_URI}/${photoId}/gallery`;
          const { width, height, img } = await getImageSize(url);
          const orientation =
            width > height
              ? ImageOrientation.LANDSCAPE
              : ImageOrientation.PORTRAIT;
          return {
            photoId,
            img,
            width,
            height,
            orientation,
            rowSpan: orientation === ImageOrientation.LANDSCAPE ? 1 : 2,
            modalSize:
              orientation === ImageOrientation.LANDSCAPE ? '6xl' : 'lg',
          };
        }),
      );
      setImages(imageData);
    };

    loadImages().catch((e) => e);
  }, [photos]);

  return (
    <Box onKeyDown={handleOnKeyDown}>
      {modalPhotoId && (
        <Modal
          size={
            images.filter((img) => img.photoId === modalPhotoId)[0].modalSize
          }
          onClose={() => {
            setModalPhotoId(null);
          }}
          isOpen={!!modalPhotoId}
        >
          <ModalOverlay />
          <ModalContent>
            <Image
              onClick={() => {
                setDisplayImageBorder((prevState: boolean) => !prevState);
              }}
              style={{
                cursor: 'pointer',
                margin: `${displayImageBorder ? '12px 12px 12px 12px' : '0'}`,
                backgroundColor: 'white',
              }}
              src={`${IMAGE_URI}/${modalPhotoId}/main`}
            />
          </ModalContent>
        </Modal>
      )}
      <Button
        style={{ display: user ? 'block' : 'none' }}
        onClick={() => {
          if (user) {
            navigate(paths.uploadPhoto);
          }
        }}
      >
        Create
      </Button>
      <Center>
        <Grid templateColumns="repeat(3, 1fr)" gap={5}>
          {images.map(({ photoId, img, rowSpan }) => {
            return (
              <GridItem
                key={photoId}
                style={{ cursor: 'pointer' }}
                colSpan={1}
                rowSpan={rowSpan}
              >
                <Box style={{ cursor: 'pointer' }} key={photoId}>
                  <FontAwesomeIcon
                    onClick={() => deletePhoto({ photoId })}
                    size="sm"
                    icon={faTrash}
                    color="#ff575c"
                    style={{
                      cursor: 'pointer',
                      float: 'right',
                      marginTop: '5px',
                      display: user ? 'block' : 'none',
                    }}
                  />
                </Box>
                <Image
                  src={img.src}
                  onClick={() => {
                    setModalPhotoId(photoId);
                  }}
                />
              </GridItem>
            );
          })}
        </Grid>
      </Center>
    </Box>
  );
};
